import { FC } from 'react'

import { Trash } from '@syconium/little-miss-figgy'

import { useCart } from '../../../../app/_providers/CartProvider.client'
import { useSiteWideMessaging } from '../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryGroupItem } from '../CartItem/EmbroideryGroupItem/EmbroideryGroupItem'
import { KitCartItem } from '../CartItem/KitCartItem/KitCartItem'
import { PriceAndDeleteButton } from '../CartItem/PriceAndDeleteButton/PriceAndDeleteButton'
import { DeleteIconButton, DeleteIconColumn } from '../CartItem/PriceAndDeleteButton/styles'
import { CartItemQuantity } from '../CartItemQuantity/CartItemQuantity'
import { CartEntry, hasPlaceholderLine, isLogoSetupInCart, orderKitEntries } from '../utils'

import { KitGroupPlaceholder } from './KitGroupPlaceholder'
import {
  Body,
  Details,
  Items,
  PriceSection,
  QuantityWrapper,
  SiteWidePromoWrap,
  Title,
} from './styles'

type KitGroupProps = {
  entries: ReadonlyArray<CartEntry>
  decrementQuantity(): void
  embroideryLabel: string
  eventCategory: 'minicart' | 'cart'
  incrementQuantity(): void
  removeAll(): void
  removeItem(key: string): void
  setQuantity(newQuantity: number): void
}

export const KitGroup: FC<KitGroupProps> = ({
  entries,
  decrementQuantity,
  embroideryLabel,
  eventCategory,
  incrementQuantity,
  removeAll,
  removeItem,
  setQuantity,
}) => {
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()
  const {
    cart: { removeItemLabel },
  } = useTranslation()
  const { cart, status: cartStatus } = useCart()

  if (entries.some(hasPlaceholderLine)) return <KitGroupPlaceholder entries={entries} />

  const firstItem = entries.find(e => e.item.properties._kit)?.item
  const kitTitle = firstItem?.properties._kit || ''
  const removeItemAriaLabel = removeItemLabel.replace('{{itemTitle}}', kitTitle)
  const cartContainsLogoSetup = isLogoSetupInCart(Object.values(cart.items))

  const discountPct = parseFloat(entries[0]?.item.properties._kitDiscountPct ?? '')

  // combined price on the kit group level excludes embroidery items in the group
  // because those price are listed separately and included in the grand total
  const combinedFullPrice = entries.reduce(
    (total, entry) => total + (discountPct ? entry.item.fullPrice : entry.item.effectivePrice),
    0
  )

  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const swPromoEligible = entries[0]?.item.properties._swPromoEligible === 'true'

  const combinedEffectivePrice = validDiscountPct
    ? Math.round(combinedFullPrice * ((100 - discountPct) / 100))
    : combinedFullPrice

  // Kit embroidery panels render after all garments, in garment order.
  const orderedCartEntries = orderKitEntries(entries)

  return (
    <Body data-testid='kit-group' data-cart-item-id={entries[0]?.item.properties._setUid}>
      <Details>
        <Title>{kitTitle}</Title>
        <DeleteIconColumn>
          <DeleteIconButton onClick={removeAll} aria-label={removeItemAriaLabel}>
            <Trash stroke='currentColor' />
          </DeleteIconButton>
        </DeleteIconColumn>
      </Details>

      {siteWidePromo && swPromoEligible && <SiteWidePromoWrap>{siteWidePromo}</SiteWidePromoWrap>}

      <Details>
        <QuantityWrapper>
          <CartItemQuantity
            decrementQuantity={decrementQuantity}
            incrementQuantity={incrementQuantity}
            setQuantity={setQuantity}
            quantity={firstItem?.quantity || 0}
            cartIsUpdating={cartStatus === 'pending'}
          />
        </QuantityWrapper>
        <PriceSection>
          <PriceAndDeleteButton
            fullPrice={combinedFullPrice}
            effectivePrice={combinedEffectivePrice}
          />
        </PriceSection>
      </Details>

      <Items>
        {orderedCartEntries.map(e => (
          <KitCartItem key={e.item.key} cartItem={e.item} eventCategory={eventCategory} />
        ))}
        {orderedCartEntries.map(
          e =>
            e.embroidery && (
              <EmbroideryGroupItem
                key={e.embroidery.guid}
                group={e.embroidery}
                inKit
                isLogoSetupInCart={cartContainsLogoSetup}
                label={embroideryLabel}
                removeGroup={() => removeItem(e.embroidery!.lines[0]!.key)}
              />
            )
        )}
      </Items>
    </Body>
  )
}
